// React imports
import { lazy, Suspense, useEffect, useState, useCallback, useMemo } from 'react';
import { IntlProvider } from 'react-intl';

// Third-party library imports
import { Provider as ReactSpectrumProvider, defaultTheme } from '@adobe/react-spectrum';
import { Provider as ReactSpectrumS2Provider } from '@react-spectrum/s2';
import { ToastContainer } from '@react-spectrum/toast';
import { Route, Routes, Navigate } from 'react-router-dom';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';

// Service imports
import IMS from './services/ims';
import LCSServices from './services/LCSServices';
import { logData } from './services/logger/logData';
import { LOCALIZED_PRODUCTS } from './services/permissions/constants/products';

// Class imports
import AppID from './classes/AppID';
import Features from './classes/Features';

// Store imports
import { useLCPSelector } from './store/hooks';
import { updateLocaleUtut, updateLocaleUI, updateLoginStatus, updateUserApps } from './store/reducers/appSlice';
import {
    setCpRoles,
    setTbwRoles,
    updateClearUserData,
    updateCpSupportedLocales,
    updateTbwSupportedLocales,
    updateUserData,
} from './store/reducers/userSlice';
import type { DomainRoles } from './store/reducers/userSlice';

// Utils and constants
import { getAppURIPath, isIMSStageEnvironment } from './utils/constants';
import { localizedMessages, stringsLocal } from './Localization';

// Component imports
import ErrorBoundary from './components/ErrorBoundary';

// Lazy-loaded view imports
const HeaderNav = lazy(() => import('./views/HeaderNav/HeaderNav'));
const TBWCreateView = lazy(() => import('./views/Create/TBWCreateView'));

// Error Pages
const NotAuthorized = lazy(() => import('./views/Error/Not-Authorized'));
const NotFound = lazy(() => import('./views/Error/Not-Found'));
const SomethingWentWrong = lazy(() => import('./views/Error/Something-Went-Wrong'));
const PageRenderError = lazy(() => import('./views/Error/PageRenderError'));

// IMS
const ImsClient = lazy(() => import('./views/ImsClient/ImsClient'));

function App() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const currentUILoc = useLCPSelector((state) => state.app.localeUI);
    const currentTab = useLCPSelector((state) => state.app.currentTab);
    const defaultLocale = 'en_US';
    const [locale, setLocale] = useState<string>(defaultLocale);

    const nonPermRoutes = useMemo(
        () => [
            { key: 'something-went-wrong', ui: <SomethingWentWrong /> },
            { key: 'not-found', ui: <NotFound /> },
            { key: 'not-authorized', ui: <NotAuthorized /> },
            { key: 'page-render-error', ui: <PageRenderError /> },
        ],
        [],
    );

    const fetchPermissions = useCallback(async () => {
        // Capture the path at the start. fetchPermissions is async (network calls),
        // and the user may navigate to other authenticated routes (e.g. /tbw-create)
        // while it's still in flight. Only auto-redirect to dashboard if the user
        // started on login/root — otherwise leave them where they navigated.
        const initialPath = window.location.pathname;
        try {
            const ims = IMS.getInstance();
            const isLoggedIn = ims.isLoggedIn();

            if (!isLoggedIn) {
                dispatch(updateLoginStatus(''));
                dispatch(updateClearUserData(true));
                setTimeout(() => {
                    ims.login();
                }, 1000);
                return;
            } else {
                await ims.getAccessToken();
            }

            const lcsService = new LCSServices();

            // Fetch user entity from both Learn Content Service and InApp Authoring Service
            const [lcsEntityResponse, inappEntityResponse] = await Promise.all([
                lcsService.fetchUserEntityFromLCS('lcp', 'user'),
                lcsService.fetchUserEntityFromInApp('tbw', 'user'),
            ]);

            // Check if we have valid entity responses
            // Note: It's valid for a user to have roles in only one service or none at all
            const lcsSuccess = lcsEntityResponse?.data && !lcsEntityResponse.failed;
            const inappSuccess = inappEntityResponse?.data && !inappEntityResponse.failed;

            console.log('LCS Success:', lcsSuccess, 'InApp Success:', inappSuccess);

            // Extract user data from entity response
            // userDetails is nested under attributes
            // If user has no entity in a service, that's okay - we'll use data from the other service
            const lcsUserData = lcsEntityResponse?.data?.attributes?.userDetails || {};
            const inappUserData = inappEntityResponse?.data?.attributes?.userDetails || {};
            const userData = { ...lcsUserData, ...inappUserData };

            // Only dispatch user data if we have some
            if (Object.keys(userData).length > 0) {
                dispatch(updateUserData(userData));
            }

            // Capture per-domain supported locales from each entity's attributes.locales.
            // - tbwSupportedLocales drives the TBW Tutorial Language dropdown filter.
            // - cpSupportedLocales disables (not hides) target-locale checkboxes in CP
            //   localization dialogs (RequestLocalization, RefreshUtut, etc.).
            // Empty/missing array means "no filter applied" in both cases.
            const inappLocales = inappEntityResponse?.data?.attributes?.locales;
            if (Array.isArray(inappLocales)) {
                dispatch(updateTbwSupportedLocales(inappLocales));
            }
            const lcsLocales = lcsEntityResponse?.data?.attributes?.locales;
            if (Array.isArray(lcsLocales)) {
                dispatch(updateCpSupportedLocales(lcsLocales));
            }

            // Extract roles from both entity responses
            // Roles are nested under attributes.roles and organized by role type
            // Structure: { "author": [...], "curator": [...], "readonly": [...], "admin": [...] }
            // Each value is an array of permission entries:
            //   - AllPermissions:  { type: "AllPermissions" }                    (admin / readonly)
            //   - AppPermissions:  { type: "AppPermissions", sapCode, fiCode }   (per-app workflow role)
            const lcsRoles = lcsEntityResponse?.data?.attributes?.roles || {};
            const inappRoles = inappEntityResponse?.data?.attributes?.roles || {};
            console.log('LCS Roles:', lcsRoles, 'InApp Roles:', inappRoles);

            // Build a DomainRoles snapshot for one service. Each domain independently tracks:
            //   - isAdmin / isReadonly: AllPermissions flags (set by their own AllPermissions entry)
            //   - roleByApp: AppPermissions per app (last-write-wins on duplicate appName,
            //     iterated in workflowRoleTypes priority order so e.g. CURATOR overrides AUTHOR)
            const buildDomainRoles = (
                roles: Record<string, unknown>,
                workflowRoleTypes: string[],
            ): { domain: DomainRoles; allowedApps: string[] } => {
                const domain: DomainRoles = { isAdmin: false, isReadonly: false, roleByApp: {} };
                const allowedApps: string[] = [];
                const allowedAppNames = new Set(AppID.all().map((a) => a.appName));

                // Normalize keys to uppercase so 'readOnly', 'readonly', 'READONLY' all match.
                const normalizedRoles: Record<string, unknown> = {};
                for (const key of Object.keys(roles)) {
                    normalizedRoles[key.toUpperCase()] = roles[key];
                }

                const isAllPermissions = (key: string): boolean => {
                    const arr = normalizedRoles[key.toUpperCase()];
                    if (!Array.isArray(arr) || arr.length === 0) return false;
                    return arr.some((entry: { type?: string }) => entry.type === 'AllPermissions');
                };

                if (isAllPermissions('ADMIN')) domain.isAdmin = true;
                if (isAllPermissions('READONLY')) domain.isReadonly = true;

                for (const roleType of workflowRoleTypes) {
                    const arr = roles[roleType];
                    if (!Array.isArray(arr)) continue;
                    const roleName = roleType.toUpperCase();

                    for (const entry of arr as Array<{ type?: string; sapCode?: string; fiCode?: string }>) {
                        if (entry.type !== 'AppPermissions') continue;
                        // Prefer fiCode (disambiguates PSWeb=photoshop_web vs PSDesktop=photoshop_cc,
                        // both of which share sapCode=PHSP). Fall back to sapCode for legacy backends.
                        const app = entry.fiCode ? AppID.find(entry.fiCode) : undefined;
                        const appName = app?.appName ?? entry.sapCode;
                        if (!appName) continue;

                        domain.roleByApp[appName] = roleName;
                        if (app || allowedAppNames.has(appName)) allowedApps.push(appName);
                    }
                }
                return { domain, allowedApps };
            };

            // CP workflow roles live in LCS; TBW workflow roles live in InApp. Both domains
            // support the same five workflow role types under Cedar (author/curator/qe/locqe/production).
            const WORKFLOW_ROLE_TYPES = ['author', 'curator', 'qe', 'locqe', 'production'];
            const cpResult = buildDomainRoles(lcsRoles, WORKFLOW_ROLE_TYPES);
            const tbwResult = buildDomainRoles(inappRoles, WORKFLOW_ROLE_TYPES);

            dispatch(setCpRoles(cpResult.domain));
            dispatch(setTbwRoles(tbwResult.domain));
            console.log('CP roles:', cpResult.domain, 'TBW roles:', tbwResult.domain);

            // appsWithRoles drives the TBW app dropdown only — CP doesn't read it. Including
            // CP-only apps here would surface them in the TBW product picker, even though the
            // user has no TBW role on those apps. Dispatch only the TBW per-app set.
            //
            // Readonly users see all known TBW apps (readonly browses All Published which is
            // per-app, so they need every app in the dropdown). This applies whether they have
            // ONLY readonly (no AppPermissions entries) OR readonly combined with workflow
            // roles on some apps — in the combined case, the workflow apps still need readonly
            // browse on the others, so the dropdown must include all apps regardless.
            const tbwApps = tbwResult.domain.isReadonly
                ? AppID.all().map((a) => a.appName)
                : [...new Set(tbwResult.allowedApps)];
            if (tbwApps.length > 0) {
                dispatch(updateUserApps(tbwApps));
                console.log('Set TBW Apps:', tbwApps);
            }

            // If we reached here, the user is IMS-authenticated (passed isLoggedIn + getAccessToken).
            // Always allow them to reach the dashboard — even with no roles/entity.
            // New users will see the Account & Permissions tab to request access.
            // Users with roles will see their authorized tabs (CP, TBW, User Management).
            dispatch(updateLoginStatus('success'));

            // Only auto-redirect to dashboard if the user started on login/root.
            // If they navigated to another authenticated route (e.g. /tbw-create) while
            // fetchPermissions was still running, don't kick them back.
            const onLoginOrRoot =
                initialPath === '/' ||
                initialPath === '' ||
                initialPath.indexOf(getAppURIPath('login')) === 0;
            if (!onLoginOrRoot) {
                console.log('fetchPermissions complete, leaving user on', initialPath);
                return;
            }

            console.log('navigate to dashboard', getAppURIPath('dashboard'));
            return navigate(getAppURIPath('dashboard'));
        } catch (ex) {
            dispatch(updateClearUserData(true));
            await logData({
                request: {} as Record<string, unknown>,
                response: ex,
                type: 'Login error',
                failed: true,
            });
            console.log('Login error:', ex);
            return navigate(getAppURIPath('something-went-wrong'));
        }
    }, [dispatch, navigate]);

    const setupFeatureFlags = useCallback(() => {
        // TODO add query string check
        Features.add(Features.Name.LANG_SELECT);
        // Features.add(Features.Name.EXPRESS)
        // Features.add(Features.Name.ILLUSTRATOR)
        Features.checkUrl();
        // Override language
        const params = new URLSearchParams(document.location.search);
        const locale = params.get('locale') ?? '';
        const validLocales = ['en_US', 'de_DE', 'es_ES', 'fr_FR', 'pt_PT', 'ja_JP', 'ko_KR', 'zh_CN', 'zh_TW'];
        if (validLocales.includes(locale)) {
            dispatch(updateLocaleUI(locale));
            dispatch(updateLocaleUtut(locale));
        }
    }, [dispatch]);

    useEffect(() => {
        setupFeatureFlags();
        console.log('window.location.pathname', window.location.pathname);
        if (
            window.location.pathname.length > 1 &&
            nonPermRoutes.filter((obj) => window.location.pathname.indexOf(getAppURIPath(obj.key)) == 0).length > 0
        ) {
            return;
        }
        console.log('fetchPermissions', window.location.pathname);
        void fetchPermissions();
    }, [fetchPermissions, nonPermRoutes, setupFeatureFlags]);

    const PrivateRoute = ({ path, children }: { path: string; children: React.ReactNode }) => {
        const auth = IMS.getInstance().isLoggedIn();
        return auth ? children : <Navigate to="/cc/lcp/login" state={{ originPath: path }} />;
    };

    useEffect(() => {
        if (LOCALIZED_PRODUCTS.includes(currentTab as string)) {
            setLocale(currentUILoc);
            return;
        }
        setLocale(defaultLocale);
    }, [currentUILoc, currentTab]);

    return (
        <ReactSpectrumProvider theme={defaultTheme} colorScheme="light">
            <ReactSpectrumS2Provider colorScheme="light">
                <IntlProvider
                    locale={stringsLocal(locale)}
                    defaultLocale={stringsLocal(defaultLocale)}
                    messages={localizedMessages(locale)}
                >
                    <ErrorBoundary>
                        <Suspense fallback={<div></div>}>
                            <Routes>
                                <Route path={getAppURIPath('login')} Component={HeaderNav} />
                                <Route
                                    path={getAppURIPath('dashboard')}
                                    element={
                                        <PrivateRoute path={getAppURIPath('dashboard')}>
                                            <HeaderNav />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path={getAppURIPath('tbw-create')}
                                    element={
                                        <PrivateRoute path={getAppURIPath('tbw-create')}>
                                            <TBWCreateView />
                                        </PrivateRoute>
                                    }
                                />
                                {nonPermRoutes.map((obj) => (
                                    <Route
                                        key={obj.key}
                                        path={getAppURIPath(obj.key)}
                                        element={<PrivateRoute path={getAppURIPath(obj.key)}>{obj.ui}</PrivateRoute>}
                                    />
                                ))}

                                {/* IMS Routes */}
                                {isIMSStageEnvironment() && (
                                    <Route
                                        path={'/cc/lcp/ims-client'}
                                        element={
                                            <PrivateRoute path={'/cc/lcp/ims-client'}>
                                                <ImsClient />
                                            </PrivateRoute>
                                        }
                                    />
                                )}

                                <Route
                                    element={
                                        <PrivateRoute path="">
                                            <NotFound />
                                        </PrivateRoute>
                                    }
                                />
                            </Routes>
                            <ToastContainer />
                        </Suspense>
                    </ErrorBoundary>
                </IntlProvider>
            </ReactSpectrumS2Provider>
        </ReactSpectrumProvider>
    );
}

export default App;
